import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import { getAccountAuditSummary } from "../services/crmService";

import "./accounts.css";

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

function StatCard({ title, value, tone = "" }) {
    return (
        <div className={`account-summary-card ${tone}`}>
            <span>{title}</span>
            <strong>{value ?? 0}</strong>
        </div>
    );
}

function StatusBadge({ value }) {
    const normalized = String(value || "").toLowerCase();
    const tone = normalized === "failed"
        ? "failed"
        : normalized === "completed" || normalized === "generated"
            ? "live"
            : "idle";

    return <span className={`account-status-pill ${tone}`}>{value || "-"}</span>;
}

export default function AccountAuditPage() {
    const { accountId } = useParams();

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                setError("");

                const data = await getAccountAuditSummary(accountId);

                if (mounted) {
                    setSummary(data);
                }
            } catch (err) {
                console.error(err);

                if (mounted) {
                    setError("Failed to load account audit.");
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [accountId]);

    const failureRate = useMemo(() => {
        if (!summary?.totalDeployments) return 0;
        return Math.round((summary.failedDeployments / summary.totalDeployments) * 100);
    }, [summary]);

    if (loading) {
        return (
            <PageShell title="Account Audit">
                <div className="account-loading-card">Loading account audit...</div>
            </PageShell>
        );
    }

    if (error) {
        return (
            <PageShell title="Account Audit">
                <div className="account-loading-card">{error}</div>
            </PageShell>
        );
    }

    if (!summary) {
        return (
            <PageShell title="Account Audit">
                <div className="account-loading-card">Account audit not found.</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Account Audit">
            <div className="account-audit-shell">
                <section className="account-hero-card">
                    <div>
                        <span className="project-kicker">Super Admin Usage Audit</span>
                        <h1>{summary.accountName || "Account"}</h1>
                        <p>{summary.accountEmail}</p>

                        <div className="account-hero-meta">
                            <span>Account ID: {summary.accountId}</span>
                            <span>Last activity: {formatDate(summary.lastActivityAtUtc)}</span>
                            <span>Failure rate: {failureRate}%</span>
                        </div>
                    </div>

                    <div className="account-privacy-card">
                        <span>Privacy Boundary</span>
                        <strong>Lead details hidden</strong>
                        <p>
                            Only lead counts are shown here. Personal lead data stays inside client CRM.
                        </p>
                    </div>
                </section>

                <section className="account-summary-grid audit">
                    <StatCard title="Projects" value={summary.totalProjects} />
                    <StatCard title="Published" value={summary.publishedProjects} />
                    <StatCard title="AI Calls" value={summary.totalAiCalls} />
                    <StatCard title="Failed AI" value={summary.failedAiCalls} tone="danger" />
                    <StatCard title="Deployments" value={summary.totalDeployments} />
                    <StatCard title="Failed Deployments" value={summary.failedDeployments} tone="danger" />
                    <StatCard title="Module Syncs" value={summary.totalModuleSyncRuns} />
                    <StatCard title="CRM Items" value={summary.totalModuleItems} />
                    <StatCard title="Lead Count" value={summary.leadCount} />
                </section>

                <section className="account-panel">
                    <div className="account-panel-header">
                        <div>
                            <span className="project-kicker">Project Usage</span>
                            <h2>Projects under this account</h2>
                            <p>Review project activity, AI calls, deployments, syncs, CRM items, and lead totals.</p>
                        </div>
                    </div>

                    {summary.projects?.length === 0 ? (
                        <div className="account-empty-state">No projects found for this account.</div>
                    ) : (
                        <div className="audit-table-wrap">
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>Project</th>
                                        <th>Industry</th>
                                        <th>Status</th>
                                        <th>AI</th>
                                        <th>Deploy</th>
                                        <th>Module Sync</th>
                                        <th>CRM Items</th>
                                        <th>Leads</th>
                                        <th>Last Activity</th>
                                        <th>Open</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.projects.map(project => (
                                        <tr key={project.projectId}>
                                            <td>
                                                <div className="account-name-cell">
                                                    <strong>{project.businessName}</strong>
                                                    <span>{project.projectId}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="account-mini-stack">
                                                    <span>{project.industry || "-"}</span>
                                                    {project.subIndustry ? <small>{project.subIndustry}</small> : null}
                                                </div>
                                            </td>
                                            <td><StatusBadge value={project.status} /></td>
                                            <td>
                                                <div className="account-mini-stack">
                                                    <span>{project.aiCalls}</span>
                                                    {project.failedAiCalls > 0 ? <small>{project.failedAiCalls} failed</small> : null}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="account-mini-stack">
                                                    <span>{project.deployments}</span>
                                                    {project.failedDeployments > 0 ? <small>{project.failedDeployments} failed</small> : null}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="account-mini-stack">
                                                    <span>{project.moduleSyncRuns}</span>
                                                    {project.failedModuleSyncRuns > 0 ? <small>{project.failedModuleSyncRuns} failed</small> : null}
                                                </div>
                                            </td>
                                            <td>{project.moduleItems}</td>
                                            <td>{project.leadCount}</td>
                                            <td>{formatDate(project.lastActivityAtUtc)}</td>
                                            <td>
                                                <Link
                                                    className="project-action-btn compact secondary"
                                                    to={`/crm/projects/${project.projectId}`}
                                                >
                                                    Open
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <section className="account-audit-grid">
                    <div className="account-panel">
                        <div className="account-panel-header">
                            <div>
                                <span className="project-kicker">Recent AI Usage</span>
                                <h2>Provider and feature activity</h2>
                            </div>
                        </div>

                        {summary.recentAiUsage?.length === 0 ? (
                            <div className="account-empty-state">No AI usage yet.</div>
                        ) : (
                            <div className="audit-list">
                                {summary.recentAiUsage.map(item => (
                                    <div className="audit-list-item" key={item.id}>
                                        <div>
                                            <strong>{item.feature || "AI call"}</strong>
                                            <span>{item.projectName}</span>
                                            <small>
                                                {item.provider || "-"} · {item.model || "-"} · {item.modality || "-"}
                                            </small>
                                        </div>

                                        <div>
                                            <StatusBadge value={item.success ? "completed" : "failed"} />
                                            <small>{item.durationMs}ms</small>
                                            <small>{formatDate(item.createdAtUtc)}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="account-panel">
                        <div className="account-panel-header">
                            <div>
                                <span className="project-kicker">Failures</span>
                                <h2>Deployment and sync issues</h2>
                            </div>
                        </div>

                        {summary.recentFailures?.length === 0 ? (
                            <div className="account-empty-state">No recent failures.</div>
                        ) : (
                            <div className="audit-list">
                                {summary.recentFailures.map(item => (
                                    <div className="audit-list-item failed" key={item.id}>
                                        <div>
                                            <strong>{item.area}</strong>
                                            <span>{item.projectName}</span>
                                            <small>
                                                {item.triggeredBy || "-"}
                                                {item.moduleKey ? ` · ${item.moduleKey}` : ""}
                                            </small>
                                            {item.errorMessage ? <p>{item.errorMessage}</p> : null}
                                        </div>

                                        <div>
                                            <StatusBadge value={item.status} />
                                            <small>{formatDate(item.createdAtUtc)}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </PageShell>
    );
}